import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { useTranslation } from "react-i18next";
import { authAPI } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => authAPI.forgotPassword(email),
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    forgotPasswordMutation.mutate(email);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 relative">
        <DnaBackground />
        
        <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-3xl font-bold text-gradient mb-4">
              {t('forgotPassword.emailSent') || 'Check Your Email'}
            </h1>
            
            <p className="text-muted-foreground mb-2">
              {t('forgotPassword.emailSentMessage') || 
                'We\'ve sent a password reset link to'}
            </p>
            
            <p className="font-semibold text-primary mb-6">
              {email}
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-sm text-muted-foreground text-left">
              <p className="mb-2">
                {t('forgotPassword.checkInbox') || 
                  'Please check your inbox and click on the link to reset your password.'}
              </p>
              <p>
                {t('forgotPassword.checkSpam') || 
                  'If you don\'t see it, check your spam folder.'}
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/auth')}
                className="w-full bg-primary hover:bg-primary/90 shadow-glow"
              >
                {t('forgotPassword.backToSignIn') || 'Back to Sign In'}
              </Button>
              
              <Button
                onClick={() => {
                  setIsSuccess(false);
                  setEmail("");
                }}
                variant="outline"
                className="w-full"
              >
                {t('forgotPassword.tryDifferentEmail') || 'Try Different Email'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <DnaBackground />
      
      <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back') || 'Back to Sign In'}
        </button>

        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          
          <h1 className="text-3xl font-bold text-gradient mb-2">
            {t('forgotPassword.title') || 'Forgot Password?'}
          </h1>
          
          <p className="text-muted-foreground">
            {t('forgotPassword.description') || 
              'No worries, we\'ll send you reset instructions.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="email"
              placeholder={t('auth.email') || 'Email'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-background/50"
              disabled={forgotPasswordMutation.isPending}
            />
          </div>

          {forgotPasswordMutation.isError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md text-start">
              {getLocalizedErrorMessage(forgotPasswordMutation.error, "errors.sendResetEmailFailed")}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 shadow-glow"
            disabled={forgotPasswordMutation.isPending}
          >
            {forgotPasswordMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading') || 'Sending...'}
              </>
            ) : (
              t('forgotPassword.sendResetLink') || 'Send Reset Link'
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
